function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import throttle from '../utils/throttle';
import styles from './LazyContainer.module.scss';
import H from '../H/H';
import I18n from '../I18n/I18n/I18n';

// How much to render above and below the scroll area
// 1 = 1 screen above and 1 below so 3x the content area
// 0.5 = 1/2 screen above and below so 2x the content area
var SCROLL_BUFFER_HEIGHT = 0.5;
function isScrolledIntoView(elem) {
  var rect = elem.getBoundingClientRect();
  var height = window.innerHeight;
  var buffer = height * SCROLL_BUFFER_HEIGHT;
  return !(rect.bottom < -buffer || rect.top > height + buffer);
}
function hasFocus(elem) {
  return elem === document.activeElement || elem.contains(document.activeElement);
}
function getHeight(elem) {
  return elem.getBoundingClientRect().height;
}
function getScrollParent(element) {
  var style = getComputedStyle(element);
  var excludeStaticParent = style.position === 'absolute';
  var overflowRegex = /(auto|scroll|hidden)/;
  if (style.position === 'fixed') return document.body;
  for (var parent = element; parent = parent.parentElement;) {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === 'static') {
      continue;
    }
    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) {
      return parent;
    }
  }
  return window;
}
var LazyContainerContext = createContext(undefined);
export function LazyContainerState(_ref) {
  var children = _ref.children;
  var context = useRef({});
  return /*#__PURE__*/React.createElement(LazyContainerContext.Provider, {
    value: context.current
  }, children);
}
export default function LazyContainer(_ref2) {
  var children = _ref2.children,
    _ref2$className = _ref2.className,
    className = _ref2$className === void 0 ? '' : _ref2$className,
    _ref2$number = _ref2.number,
    number = _ref2$number === void 0 ? 0 : _ref2$number,
    _ref2$placeholderComp = _ref2.placeholderComponent,
    placeholderComponent = _ref2$placeholderComp === void 0 ? H : _ref2$placeholderComp,
    _ref2$placeholderHeig = _ref2.placeholderHeight,
    placeholderHeight = _ref2$placeholderHeig === void 0 ? '5em' : _ref2$placeholderHeig,
    _ref2$placeholderI18n = _ref2.placeholderI18nKey,
    placeholderI18nKey = _ref2$placeholderI18n === void 0 ? '' : _ref2$placeholderI18n,
    _ref2$tabbable = _ref2.tabbable,
    tabbable = _ref2$tabbable === void 0 ? true : _ref2$tabbable,
    _ref2$tag = _ref2.tag,
    tag = _ref2$tag === void 0 ? 'div' : _ref2$tag;
  var context = useContext(LazyContainerContext) || {};
  var ref = useRef(null);
  var focusRef = useRef(false);
  var classNameRef = useRef(styles['visible']);
  var defaultEntry = useMemo(function () {
    var entry = context[number] || {
      visible: false,
      height: context[0] && context[0].height || placeholderHeight
    };
    context[number] = entry;
    if (context[number].visible) {
      classNameRef.current = undefined;
    }
    return entry;
  }, [number, context, placeholderHeight]);
  var heightRef = useRef(defaultEntry.height);
  var _useState = useState(defaultEntry.visible),
    _useState2 = _slicedToArray(_useState, 2),
    visible = _useState2[0],
    setVisible = _useState2[1];
  var onFocus = useCallback(function () {
    setVisible(true);
    focusRef.current = true;
  }, []);
  var onBlur = useCallback(function () {
    return focusRef.current = false;
  }, []);
  var style = useMemo(function () {
    return visible ? {} : {
      minHeight: heightRef.current
    };
  }, [visible]);
  var loadingI18n = useMemo(function () {
    return {
      id: placeholderI18nKey,
      values: {
        number: number
      }
    };
  }, [placeholderI18nKey, number]);
  useEffect(function () {
    if (visible && focusRef.current) {
      focusRef.current = false;
      var nodeList = ref.current.querySelectorAll('[tabindex]');
      if (nodeList.length > 0) {
        nodeList[0].focus();
      }
    }
  }, [visible]);
  useEffect(function () {
    var scrollParent = getScrollParent(ref.current);
    var handleScroll = throttle(function () {
      if (ref.current && (isScrolledIntoView(ref.current) || hasFocus(ref.current))) {
        heightRef.current = getHeight(ref.current) + 'px';
        context[number] = {
          height: heightRef.current,
          visible: true
        };
        setVisible(true);
      } else {
        context[number].visible = false;
        classNameRef.current = styles['visible'];
        setVisible(false);
      }
    }, 50, {
      leading: false
    });
    handleScroll(); // Initial Rendering
    setTimeout(handleScroll, 10); // Check again in case placeholderHeight is way off
    window.addEventListener('scroll', handleScroll); // Always add to window in case our parent has been "lazy" scrolled off as well PRO-8294
    scrollParent.addEventListener('scroll', handleScroll);
    return function () {
      window.removeEventListener('scroll', handleScroll);
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [context, number]);
  if (!visible) {
    var Heading = placeholderComponent;
    return createElement(tag, {
      ref: ref,
      style: style,
      className: className,
      tabIndex: tabbable ? '0' : undefined,
      onFocus: onFocus,
      onBlur: onBlur
    }, /*#__PURE__*/React.createElement(Heading, null, /*#__PURE__*/React.createElement("span", {
      className: styles['sr-only']
    }, /*#__PURE__*/React.createElement(I18n, {
      i18n: loadingI18n
    }))));
  }
  return children({
    ref: ref,
    className: classNameRef.current
  });
}